<script setup lang="ts">
import type { MuseumBodyProps } from '@/types/appTypes'

const props = withDefaults(defineProps<MuseumBodyProps>(), {})

const app = useNuxtApp()
const storeIndex = useStoreIndex()
const refDialog = ref(null)

const pageData = {
  sLogoImg: null, // ロゴ画像
  aSlide: null, // スライド情報
  sBody: null, // ボディ
  sAddress: null, // 住所
  aEquipCategories: null, // 設備リスト
  aBuildingTypeCategories: null, // 館種リスト
  sGmapUrl: null, // GoogleMap URL
  parsedTel: {
    number: '',
    href: '',
    label: '',
  },
}

const refData = reactive({ sSlideCaption: null })

const onChangeSlide = (slideNO) => {
  refData.sSlideCaption = pageData.aSlide[slideNO].sCaption
}

if (props.item) {
  if (props.item.custom) {
    // 住所
    if (!pageData.sAddress) {
      pageData.sAddress = app.$getAddress(props.item.custom.museum_postal_code, props.item.custom.prefecture, props.item.custom.museum_address_city, props.item.custom.museum_address_street, props.item.custom.museum_address_bldg)
    }

    // GoogleMAP URL
    if (!pageData.sGmapUrl) {
      pageData.sGmapUrl = app.$getGmapUrl(props.item.title, storeIndex.getPrefName(props.item.custom.prefecture), props.item.custom.museum_address_city, props.item.custom.museum_address_street, props.item.custom.museum_address_bldg, props.item.lat, props.item.lng)
    }

    if (props.objects) {
      // ロゴ画像
      if (!pageData.sLogoImg && props.item.custom.logo) {
        pageData.sLogoImg = storeIndex.getImgInfoByID(props.item.custom.logo, props.objects)?.url
      }

      // スライド情報
      if (import.meta.client === true) {
        if (!pageData.aSlide) {
          if (props.item?.custom && props.objects) { // 追加
            pageData.aSlide = []
            app.$addSlides(
              props.item,
              'museum_slide',
              10,
              props.objects,
              pageData.aSlide,
              (props.item.is_old === 1) ? null : 'l',
            )
          }
          if (props.item.eye_catch_image_object) {
            app.$initSlide(props.item.eye_catch_image_object, pageData.aSlide)
          }
        }

        if (pageData.aSlide?.length === 1) {
          onChangeSlide(0)
        }
      }
    }
  }

  // ボディ
  if (!pageData.sBody) {
    let strBody = app.$getArticleBody(props.item)

    if (strBody && (strBody.includes('<p>') || strBody.includes('<br>'))) {
      //  タグモード
      //  改行を取る
      strBody = app.$trimEnterTrue(strBody)
      //  brを改行にする
      strBody = app.$brToEnter(strBody)
      //  </p>を改行x2にする
      strBody = strBody.replace(/<\/p>/g, '\n')
      //  HTML要素タグを削除
      strBody = app.$stripHtmlTag(strBody)
    }
    pageData.sBody = strBody
  }

  // 設備
  if (!pageData.aEquipCategories) {
    pageData.aEquipCategories = app.$getChildCategoriesBySlug(props.item.categories, 'root_option')
  }

  // 館種
  if (!pageData.aBuildingTypeCategories) {
    pageData.aBuildingTypeCategories = app.$getChildCategoriesBySlug(props.item.categories, 'root_kind')
  }
}

const setParsedTel = () => {
  const raw = props.item.custom.museum_telephone_to_call || ''
  const match = raw.match(/[\d-]+/)

  pageData.parsedTel.number = match ? match[0] : ''
  pageData.parsedTel.href = match ? match[0].replace(/-/g, '') : ''
  pageData.parsedTel.label = match ? raw.replace(match[0], '') : ''
}
setParsedTel()
</script>

<template>
  <div>
    <div class="c-header">
      <div>
        <h1
          v-if="item?.title"
          class="c-header_title"
        >
          {{ item.title }}
        </h1>
        <p
          v-if="item?.custom?.prefecture"
          class="c-header_location"
        >
          {{ storeIndex.getPrefName(item.custom.prefecture) }}
        </p>
      </div>

      <div v-if="pageData.sLogoImg">
        <CommonImage
          class="c-header_avatar"
          v-bind:src="pageData.sLogoImg"
          error-src="/images/noimage.svg"
          object-fit="contain"
        />
      </div>
    </div>

    <client-only>
      <DialogImg
        ref="refDialog"
      />
      <div
        v-if="pageData.aSlide?.length > 0"
        class="c-carousel"
      >
        <v-carousel
          v-if="pageData.aSlide?.length > 1"
          class="IMCustomCarousel2"
          hide-delimiter-background
          cycle
          height="auto"
          v-on:update:model-value="onChangeSlide"
        >
          <v-carousel-item
            v-for="(record, index) in pageData.aSlide"
            v-bind:key="index"
          >
            <CommonImage
              class="c-carousel-item-img"
              v-bind:src="record.oImg"
              error-src="/images/noimage.svg"
              v-bind:height="app.$device.isMobile ? '236' : '481'"
              object-fit="contain"
              v-on:click="refDialog.onClickOpen(record.oImg)"
            />
          </v-carousel-item>
        </v-carousel>
        <CommonImage
          v-else
          class="c-carousel-item-img"
          v-bind:src="pageData.aSlide[0].oImg"
          error-src="/images/noimage.svg"
          v-bind:height="app.$device.isMobile ? '236' : '481'"
          object-fit="contain"
          v-on:click="refDialog.onClickOpen(pageData.aSlide[0].oImg)"
        />
      </div>

      <div
        v-if="refData.sSlideCaption"
        class="c-carousel_imgLocation"
      >
        {{ refData.sSlideCaption }}
      </div>
    </client-only>

    <div
      v-if="pageData.sBody"
      class="c-body"
    >
      <div
        class="c-body_text l-blank-icon-none"
      >
        {{ pageData.sBody }}
      </div>
    </div>

    <div class="c-info">
      <table>
        <tbody>
          <tr
            v-if="pageData.sAddress"
          >
            <td>住所</td>
            <td>
              {{ pageData.sAddress }}
            </td>
          </tr>

          <tr
            v-if="item?.custom?.museum_telephone_to_call"
          >
            <td>電話</td>
            <td>
              <span class="pc-only">{{ item.custom.museum_telephone_to_call }}</span>
              <div class="sp-only">
                <template v-if="pageData.parsedTel.number">
                  <a
                    v-bind:href="'tel:' + pageData.parsedTel.href"
                  >
                    {{ pageData.parsedTel.number }}
                  </a>{{ pageData.parsedTel.label }}
                </template>
                <template v-else>
                  {{ item.custom.museum_telephone_to_call }}
                </template>
              </div>
            </td>
          </tr>
          <tr
            v-if="item?.custom?.museum_opening_hour"
          >
            <td>開館時間</td>
            <td>
              <div v-html="app.$trimEnter(item.custom.museum_opening_hour)" />
            </td>
          </tr>
          <tr
            v-if="item?.custom?.museum_closed_period"
          >
            <td>休館日</td>
            <td>
              <div v-html="app.$trimEnter(item.custom.museum_closed_period)" />
            </td>
          </tr>
          <tr
            v-if="item?.custom?.museum_addmission_fee"
          >
            <td>料金</td>
            <td>
              <div v-html="app.$trimEnter(item.custom.museum_addmission_fee)" />
            </td>
          </tr>
          <tr
            v-if="pageData.aBuildingTypeCategories.length"
          >
            <td>館種</td>
            <td>
              <li
                v-for="(source, index) in pageData.aBuildingTypeCategories"
                v-bind:key="index"
                class="c-info_facility"
              >
                <div v-if="index !== 0">
                  、
                </div>
                <div>
                  {{ source.name }}
                </div>
              </li>
            </td>
          </tr>
          <tr
            v-if="pageData.aEquipCategories.length"
          >
            <td>設備</td>
            <td>
              <li
                v-for="(source, index) in pageData.aEquipCategories"
                v-bind:key="index"
                class="c-info_facility"
              >
                <div v-if="index !== 0">
                  、
                </div>
                <div>
                  {{ source.name }}
                </div>
              </li>
            </td>
          </tr>
          <tr
            v-if="item?.custom?.museum_access || pageData.sGmapUrl"
          >
            <td>アクセス</td>
            <td>
              <div
                v-if="item.custom.museum_access"
                v-html="app.$trimEnter(item.custom.museum_access)"
              />
              <div
                v-if="pageData.sGmapUrl"
                class="c-info_map"
              >
                <iframe
                  v-bind:src="pageData.sGmapUrl"
                  class="c-info_map_item"
                  frameborder="0"
                  scrolling="no"
                  marginheight="0"
                  marginwidth="0"
                  aspect-ratio="2"
                />
              </div>
            </td>
          </tr>
          <tr
            v-if="item?.custom?.museum_url_jp"
          >
            <td>公式サイト</td>
            <td>
              <a
                v-bind:href="item.custom.museum_url_jp"
                target="_blank"
                class="c-info_web"
              >
                {{ item.custom.museum_url_jp }}
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.c-header {
  display: flex;
  justify-content: space-between;
  font-family: var(--g_font_family_NotoSansCJKjp);

  &_title {
    margin-top: 2px;
    color: var(--color_text_base);
    font-size: 20px;
    font-weight: 400;
  }

  &_location {
    // margin-top: -6px;
    margin-top: 4px;
    color: var(--color_text_base);
    font-size: 14px;
    font-weight: bold;
  }

  &_avatar {
    min-width: 100px;
    max-width: 100px;
    height: 100px;
    margin-left: 10px;
    border: 1px solid var(--g_color_grey_main);

    @include responsive.mq_down(sp) {
      min-width: 70px;
      max-width: 70px;
      height: 70px;
    }
  }
}

.c-carousel {
  margin-top: 30px;

  &-item {
    &-img {
      position: relative;
      width: 100%;
      background-color: #ebebeb;
      height: 481px;

      // @include responsive.mq_down(sp) {
      //   height: 48vw;
      // }
    }
  }

  &_imgLocation {
    display: flex;
    margin-top: 5px;
    color: #7d7d7d;
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 14px;
    line-height: 1.4;
  }
}

.c-body {
  margin-top: 20px;
  color: var(--color_text_base);
  font-family: var(--g_font_family_NotoSansCJKjp);
  font-size: 16px;
  line-height: 1.75;

  &_text {
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 16px;
    line-height: 1.8;
    word-break: break-all;
    white-space: pre-line;
  }
}

.c-info {
  margin-top: 40px;

  &_web {
    color: var(--color_primary_link);
  }

  &_facility {
    display: inline-flex;
  }

  &_map {
    position: relative;
    width: 100%;
    margin-top: 20px;
    padding-top: 75%;

    &_item {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 14px;

    @include responsive.mq_down(sp) {
      display: block;
    }

    tr,
    th,
    td {
      @include responsive.mq_down(sp) {
        display: block;
      }
    }

    tr {
      td {
        padding: 9px 20px;
        border: 1px solid #ebebeb;
        vertical-align: top;

        @include responsive.mq_down(sp) {
          padding: 6px 10px;
          word-break: break-all;
        }

        &:first-child {
          width: 190px;
          border: 1px solid #ebebeb;
          background: #ebebeb;
          color: #252525;
          font-weight: bold;

          @include responsive.mq_down(sp) {
            width: 100%;
          }
        }

        &:last-child {
          width: calc(100% - 190px);
          line-height: 1.85;

          @include responsive.mq_down(sp) {
            width: 100%;
          }
        }
      }
    }
  }
}
</style>
